import { useEffect, useState } from "react";
import { API_BASE } from "../lib/apiBase";
import { clampPollMs, startGuardedPoll } from "../lib/pollGuard";

export type SystemStatus = "healthy" | "degraded" | "down" | "unknown";

export interface AdapterHealth {
  name: string;
  status: SystemStatus;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  consecutiveFailures: number;
  totalCalls: number;
  totalErrors: number;
  ageMinutes: number | null;
}

interface HealthResponse {
  system: {
    status: SystemStatus;
    healthy: number;
    degraded: number;
    down: number;
    total: number;
  };
  adapters: AdapterHealth[];
  mqtt: { connected: boolean; messageCount: number; lastError: string | null };
  at: string;
}

export function useSystemHealth(pollMs = 30_000) {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const poll = startGuardedPoll(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/health/detailed`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as HealthResponse;
        if (cancelled) return "skip";
        setHealth(data);
        setError(null);
        return "ok";
      } catch (err) {
        if (cancelled) return "skip";
        setError(err instanceof Error ? err.message : String(err));
        return "fail";
      }
    }, clampPollMs(pollMs));
    return () => {
      cancelled = true;
      poll.stop();
    };
  }, [pollMs]);

  return { health, error };
}
